import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stack,
  Chip,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import {
  BACKEND_URL,
  getBackendOverride,
  setBackendOverride,
} from "../utils/constants";

// Accept "host", "host:port", "http://host:port" or a full ".../api" and
// normalise to a base ending in /api (what the app appends paths to).
function normalizeBase(input) {
  let s = (input || "").trim().replace(/\/+$/, "");
  if (!s) return "";
  if (!/^https?:\/\//i.test(s)) s = "http://" + s;
  if (!/\/api$/i.test(s)) s = s + "/api";
  return s;
}

function copyText(t) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(t);
      return;
    }
  } catch (e) {
    /* fall through */
  }
  // Plain-HTTP LAN (not a secure context) — clipboard API is blocked, so fall back.
  try {
    const ta = document.createElement("textarea");
    ta.value = t;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  } catch (e) {
    /* ignore */
  }
}

export default function ConnectionSettings({ open, onClose }) {
  const override = getBackendOverride();
  const [field, setField] = useState(override || "");
  const [test, setTest] = useState(null); // {loading} | {ok,msg}
  const [info, setInfo] = useState(null); // /server-info payload
  const [infoErr, setInfoErr] = useState("");
  const [status, setStatus] = useState(null); // current connection: true/false/null
  const [copied, setCopied] = useState("");

  const refresh = useCallback(async () => {
    setStatus(null);
    setInfo(null);
    setInfoErr("");
    try {
      const r = await fetch(`${BACKEND_URL}/health`);
      setStatus(r.ok);
    } catch (e) {
      setStatus(false);
    }
    try {
      const r = await axios.get(`${BACKEND_URL}/server-info`, { timeout: 6000 });
      setInfo(r.data);
    } catch (e) {
      setInfoErr(
        e?.response?.status === 401
          ? "Log in to list the FDC's own addresses."
          : "Server addresses unavailable.",
      );
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setField(getBackendOverride() || "");
    setTest(null);
    setCopied("");
    refresh();
  }, [open, refresh]);

  const doTest = async () => {
    const base = normalizeBase(field) || BACKEND_URL;
    setTest({ loading: true });
    try {
      const r = await fetch(`${base}/health`);
      setTest({ ok: r.ok, msg: r.ok ? `Reachable — ${base}` : `HTTP ${r.status}` });
    } catch (e) {
      setTest({ ok: false, msg: e?.message || "Unreachable" });
    }
  };

  const save = () => {
    setBackendOverride(normalizeBase(field));
    window.location.reload();
  };

  const resetSameOrigin = () => {
    setBackendOverride("");
    window.location.reload();
  };

  const doCopy = (t) => {
    copyText(t);
    setCopied(t);
    window.setTimeout(() => setCopied(""), 1500);
  };

  const normalized = normalizeBase(field);
  const labelSx = { fontWeight: 700, color: "text.secondary", letterSpacing: 0.4 };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Connection / Network</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" sx={labelSx}>
              THIS DEVICE IS TALKING TO
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Chip size="small" label={BACKEND_URL} sx={{ fontFamily: "monospace" }} />
              {status === true && <Chip size="small" color="success" label="online" />}
              {status === false && <Chip size="small" color="error" label="unreachable" />}
              {status === null && <CircularProgress size={14} />}
            </Stack>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {override
                ? "Using a saved override address."
                : "Default: the same address you opened the app from."}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="caption" sx={labelSx}>
              FDC SERVER ADDRESSES — read out to gun commanders
            </Typography>
            {infoErr && (
              <Alert severity="info" sx={{ mt: 0.5 }}>
                {infoErr}
              </Alert>
            )}
            {info && (
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {info.hostname} · port {info.port}
                </Typography>
                {(info.addresses || []).map((a) => {
                  const url = `http://${a}:${info.port}`;
                  return (
                    <Stack key={a} direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={url}
                        color={a === info.primary ? "primary" : "default"}
                        sx={{ fontFamily: "monospace" }}
                      />
                      {a === info.primary && (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          primary
                        </Typography>
                      )}
                      <Tooltip title={copied === url ? "Copied" : "Copy"}>
                        <IconButton size="small" onClick={() => doCopy(url)}>
                          <ContentCopyIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  );
                })}
                {!(info.addresses || []).length && (
                  <Typography variant="caption">No external addresses detected.</Typography>
                )}
              </Stack>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="caption" sx={labelSx}>
              POINT THIS DEVICE AT AN FDC
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: "block", color: "text.secondary", mb: 0.5 }}
            >
              Only needed on a standalone gun client. Enter the FDC address, e.g.{" "}
              <code>172.23.223.64:8090</code>.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                fullWidth
                placeholder="172.23.223.64:8090"
                value={field}
                onChange={(e) => {
                  setField(e.target.value);
                  setTest(null);
                }}
              />
              <Button variant="outlined" onClick={doTest} disabled={test?.loading}>
                {test?.loading ? "…" : "Test"}
              </Button>
            </Stack>
            {test && !test.loading && (
              <Alert severity={test.ok ? "success" : "error"} sx={{ mt: 1 }}>
                {test.msg}
              </Alert>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        {override && (
          <Button color="warning" onClick={resetSameOrigin}>
            Reset to same-origin
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!field.trim() || normalized === BACKEND_URL}
        >
          Save &amp; reload
        </Button>
      </DialogActions>
    </Dialog>
  );
}
